import React, { useState, useEffect, useRef } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import styles from './Step3DocumentUpload.module.css';

interface DocumentField {
  id: string;
  label: string;
  name: string;
  fieldName: 'tax_id_document' | 'federal_id_document' | 'tobacco_license_document';
  required: boolean;
  file: File | null;
}

export const Step3DocumentUpload: React.FC = () => {
  const { uploadedFiles, addFile, removeFile, errors, setError, clearError } = useOnboardingStore();
  const prevFileCountRef = useRef<number>(0);

  const [documentFields, setDocumentFields] = useState<DocumentField[]>(() => {
    const fields: DocumentField[] = [
      {
        id: 'tax-id',
        label: 'Tax ID Document',
        name: 'taxIdDocument',
        fieldName: 'tax_id_document',
        required: true,
        file: null,
      },
      {
        id: 'fed-id',
        label: 'Federal Identification Number',
        name: 'federalIdNumber',
        fieldName: 'federal_id_document',
        required: true,
        file: null,
      },
      {
        id: 'tobacco-license',
        label: 'Tobacco License',
        name: 'tobaccoLicense',
        fieldName: 'tobacco_license_document',
        required: true,
        file: null,
      },
    ];

    // Restore uploaded files on initial mount
    if (uploadedFiles && uploadedFiles.length > 0) {
      return fields.map((field) => {
        const uploadedFile = uploadedFiles.find(
          (f) => f.fieldName === field.fieldName
        );
        if (uploadedFile) {
          if (uploadedFile.originalFile) {
            return { ...field, file: uploadedFile.originalFile };
          } else if (uploadedFile.name) {
            const syntheticFile = new File(
              ['[File content restored from storage]'],
              uploadedFile.name,
              { type: 'application/octet-stream' }
            );
            return { ...field, file: syntheticFile };
          }
        }
        return field;
      });
    }

    return fields;
  });

  // Validate that all required documents are uploaded
  useEffect(() => {
    const currentFileCount = uploadedFiles.length;
    const hasDocumentError = errors.has('documents');

    // Only update errors if file count actually changed
    if (currentFileCount !== prevFileCountRef.current) {
      prevFileCountRef.current = currentFileCount;

      if (currentFileCount < 3 && currentFileCount > 0 && !hasDocumentError) {
        // Only set error if one doesn't already exist
        setError('documents', `Please upload all 3 required documents (currently ${currentFileCount}/3)`);
      } else if ((currentFileCount >= 3 || currentFileCount === 0) && hasDocumentError) {
        // Only clear error if one exists
        clearError('documents');
      }
    }
  }, [uploadedFiles.length, errors, setError, clearError]);

  const handleFileChange = (fieldId: string, file: File | null) => {
    if (!file) return;

    // Get the fieldName from the document field
    const documentField = documentFields.find(f => f.id === fieldId);
    const fieldName = documentField?.fieldName || 'tax_id_document';

    setDocumentFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, file } : field
      )
    );

    // Add file to store with the actual File object and fieldName
    // Map fieldName to documentType for proper categorization
    const typeMap: Record<string, any> = {
      'tax_id_document': 'tax_id_document',
      'federal_id_document': 'federal_id_document',
      'tobacco_license_document': 'tobacco_license_document',
    };

    const newFile = {
      id: `${fieldId}-${Date.now()}`,
      name: file.name,
      type: (typeMap[fieldName] || 'business_license') as any,
      url: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      size: file.size,
      originalFile: file, // Store the actual File object for submission
      fieldName: fieldName, // Store the backend field name
    };

    addFile(newFile);
  };

  const handleFileRemove = (fieldId: string) => {
    const field = documentFields.find((f) => f.id === fieldId);
    if (field?.file) {
      const fileToRemove = uploadedFiles.find((f) => f.name === field.file?.name);
      if (fileToRemove) {
        removeFile(fileToRemove.id);
      }
    }

    setDocumentFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, file: null } : field
      )
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.iconBox}>
          <svg
            className={styles.icon}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v2H6V4zm0 4h8v6H6V8z" />
          </svg>
        </div>
        <div>
          <h2 className={styles.title}>
            Upload Documents
          </h2>
          <p className={styles.subtitle}>
            Please upload the required business documents
          </p>
        </div>
      </div>

      <div className={styles.formGroup}>
        {documentFields.map((field) => (
          <div key={field.id} className={styles.documentField}>
            <label className={styles.fieldLabel}>
              {field.label}
              {field.required && <span className={styles.required}>*</span>}
            </label>

            {field.file ? (
              <div className={styles.fileDisplay}>
                <div className={styles.fileName}>
                  📄 {field.file.name}
                </div>
                <button
                  type="button"
                  onClick={() => handleFileRemove(field.id)}
                  className={styles.removeButton}
                >
                  Remove
                </button>
              </div>
            ) : (
              <label className={styles.uploadBox}>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(field.id, e.target.files?.[0] || null)}
                  className={styles.fileInput}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <div className={styles.uploadContent}>
                  <div className={styles.uploadIcon}>📁</div>
                  <div className={styles.uploadText}>
                    <span className={styles.uploadMain}>Click to upload</span>
                    <span className={styles.uploadSub}>or drag and drop</span>
                  </div>
                  <div className={styles.uploadFileType}>
                    PDF, DOC, DOCX, JPG, PNG
                  </div>
                </div>
              </label>
            )}
          </div>
        ))}
      </div>

      {/* Document Upload Status */}
      <div className={styles.uploadStatus}>
        <p className={styles.statusText}>
          Documents uploaded: <strong>{uploadedFiles.length}/3</strong>
        </p>
        {errors.has('documents') && (
          <p className={styles.errorMessage}>{errors.get('documents')}</p>
        )}
      </div>

      {/* Information Box */}
      <div className={styles.infoBox}>
        <div className={styles.infoContent}>
          <div className={styles.infoIcon}>
            <svg
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className={styles.infoTitle}>
              Document Requirements
            </p>
            <p className={styles.infoText}>
              Please upload clear, legible scans or photos of your business documents.
              All files must be in PDF, DOC, DOCX, JPG, or PNG format.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
